import type { ComponentPropsWithoutRef, CSSProperties, ElementType, ReactNode } from 'react'
import { cn } from '@/lib/cn'
import styles from './Stack.module.css'

type Align = 'start' | 'center' | 'end' | 'stretch' | 'baseline'
type Justify = 'start' | 'center' | 'end' | 'between'

type PassThrough = Omit<ComponentPropsWithoutRef<'div'>, 'className' | 'style' | 'children'>

export interface StackProps extends PassThrough {
  readonly as?: ElementType | undefined
  readonly direction?: 'row' | 'column' | undefined
  /** Gap in token steps (1 = 4px). */
  readonly gap?: number | undefined
  readonly align?: Align | undefined
  readonly justify?: Justify | undefined
  readonly wrap?: boolean | undefined
  readonly grow?: boolean | undefined
  readonly className?: string | undefined
  readonly style?: CSSProperties | undefined
  readonly children: ReactNode
}

const alignClass: Record<Align, string> = {
  start: styles.alignStart!,
  center: styles.alignCenter!,
  end: styles.alignEnd!,
  stretch: styles.alignStretch!,
  baseline: styles.alignBaseline!,
}
const justifyClass: Record<Justify, string> = {
  start: styles.justifyStart!,
  center: styles.justifyCenter!,
  end: styles.justifyEnd!,
  between: styles.justifyBetween!,
}

export function Stack({
  as: Tag = 'div',
  direction = 'column',
  gap = 0,
  align,
  justify,
  wrap = false,
  grow = false,
  className,
  style,
  children,
  ...rest
}: StackProps) {
  return (
    <Tag
      className={cn(
        styles.stack,
        direction === 'row' ? styles.row : styles.column,
        align && alignClass[align],
        justify && justifyClass[justify],
        wrap && styles.wrap,
        grow && styles.grow,
        className,
      )}
      style={{ gap: `calc(var(--pb-space-1) * ${gap})`, ...style }}
      {...rest}
    >
      {children}
    </Tag>
  )
}
